import { Injectable, inject, signal, computed } from '@angular/core';
import { ConfigService } from '../../core/services/config.service';
import { AuthService } from '../../core/services/auth.service';
import { AuditService } from '../../core/services/audit.service';
import { LoyaltySubscription } from '../../core/models/loyalty/loyalty-subscription.model';
import { SubscriptionsPointsFactor } from '../../core/models/loyalty/plan.model';
import { LoyaltyMembership } from '../../core/models/account/account.model';
import { Award } from '../../core/models/loyalty/award.model';
import { DisplayPerk } from './display-perk.model';
import { LoyaltyPerk } from '../../core/models/loyalty/loyalty-subscription.model';

@Injectable({
  providedIn: 'root',
})
export class PremiumService {
  private configService = inject(ConfigService);
  private authService = inject(AuthService);
  private auditService = inject(AuditService);

  private _currentMembership = signal<LoyaltyMembership | null>(null);

  readonly subscriptions = computed(() => {
    const loyaltyState = this.configService.loyaltyState();
    if (!loyaltyState?.loyaltySubscriptions) return [];
    return loyaltyState.loyaltySubscriptions
      .map((sub: any) => {
        if (sub instanceof LoyaltySubscription) return sub;
        return new LoyaltySubscription().deserialize(sub);
      })
      .filter((sub: LoyaltySubscription) => sub.active === true);
  });
  readonly currentMembership = computed(() => this._currentMembership());

  readonly currentPlan = computed(() => {
    const membershipPlan = this._currentMembership()?.loyaltyPlan;
    if (!membershipPlan) return undefined;
    return this.subscriptions().find((sub) => sub.subscriptionName === membershipPlan);
  });

  readonly subscriptionsPointsFactors = computed<SubscriptionsPointsFactor[]>(
    () => this.configService.loyaltyState()?.subscriptionsPointsFactors ?? []
  );

  readonly awardsForSubscription = computed(() => {
    const awards: Award[] = this.configService.loyaltyState()?.awards ?? [];
    const result = new Map<string, Award[]>();

    for (const sub of this.subscriptions()) {
      const key = sub.subscriptionName ?? '';
      const filtered = awards.filter((a) => a.loyaltySubscription === key);
      result.set(key, filtered);
    }

    return result;
  });

  readonly hasRewardsToShow = computed(() =>
    Array.from(this.awardsForSubscription().values()).some((awards) => awards.length > 0)
  );

  readonly freePlanSubscription = computed(() => {
    const sub = new LoyaltySubscription();
    sub.displayName = 'Free';
    sub.description = 'Start earning rewards with every order.';
    return sub;
  });

  readonly isFreePlanCurrent = computed(() => !this.currentPlan() && !!this._currentMembership());

  private readonly comparablePerkNames = computed((): string[] => {
    const names: string[] = [];
    const seen = new Set<string>();

    for (const sub of this.subscriptions()) {
      for (const perk of (sub.perks ?? []) as LoyaltyPerk[]) {
        if (perk.perkType === 'advanced') continue;
        const name = perk.perkName ?? '';
        if (name && !seen.has(name)) {
          seen.add(name);
          names.push(name);
        }
      }
    }

    return names;
  });

  readonly freePlanPerks = computed((): DisplayPerk[] => {
    const factors = this.subscriptionsPointsFactors();
    const perks: DisplayPerk[] = [];

    const basicFactor = factors.find((f) => f.subscriptionName === 'basic');
    if (basicFactor) {
      perks.push({ text: 'Points per $1', factor: `${basicFactor.pointsPerDollar}x` });
    }

    return [...perks, ...this.excludedPerks(new Set<string>())];
  });

  readonly perksForSubscription = computed(() => {
    const factors = this.subscriptionsPointsFactors();
    const result = new Map<string, DisplayPerk[]>();

    for (const sub of this.subscriptions()) {
      const key = sub.subscriptionName ?? '';
      const perks: DisplayPerk[] = [];
      const includedNames = new Set<string>();

      const pointsFactor = factors.find((f) => f.subscriptionName === key);
      if (pointsFactor) {
        perks.push({ text: 'Points per $1', factor: `${pointsFactor.pointsPerDollar}x` });
      }

      for (const perk of sub.perks ?? []) {
        const name = perk.perkName ?? '';
        includedNames.add(name);

        let text = name;
        if (perk.price && perk.price > 0) {
          text += ` \u2013 $${(perk.price / 100).toFixed(2)}`;
        } else if (perk.percentOff && perk.percentOff > 0) {
          text += ` \u2013 ${perk.percentOff}% off`;
        }
        perks.push({ text });
      }

      result.set(key, [...perks, ...this.excludedPerks(includedNames)]);
    }

    return result;
  });

  private excludedPerks(includedNames: Set<string>): DisplayPerk[] {
    return this.comparablePerkNames()
      .filter((name) => !includedNames.has(name))
      .map((name) => ({ text: name, disabled: true }));
  }

  loadCurrentMembership(): void {
    const account = this.authService.state().account;
    if (account?.loyaltyMembership) {
      this._currentMembership.set(account.loyaltyMembership);
    }
  }

  auditSubscriptionSelected(subscription: LoyaltySubscription): void {
    this.auditService.createAudit(() => ({
      message: 'Premium subscription selected',
      details: {
        subscriptionName: subscription.subscriptionName,
        price: subscription.monthlyPrice?.price,
      },
    }));
  }
}
